import { forwardRef, type InputHTMLAttributes } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The one search box used across the app: magnifier, clear button, same height as the toolbar
 * buttons. `className` sizes the box (e.g. "w-full sm:w-64"); every other prop goes to the
 * <input>, so `value` / `onChange(e)` work exactly like a plain input.
 */
const SearchInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { dark?: boolean }>(
  ({ className, dark, value, onChange, placeholder = "Search…", ...props }, ref) => {
    const clear = () => onChange?.({ target: { value: "" }, currentTarget: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
    return (
      <div className={cn("relative", className)}>
        <Search className={cn("pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2", dark ? "text-gray-500" : "text-gray-400")} />
        <input
          ref={ref}
          type="search"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          aria-label={props["aria-label"] ?? placeholder.replace(/…|\.\.\.$/, "")}
          className={cn(
            "h-9 w-full rounded-lg border pl-9 pr-8 text-xs shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/30 [&::-webkit-search-cancel-button]:hidden",
            dark ? "border-gray-700 bg-gray-900 text-white placeholder:text-gray-500 focus:border-primary" : "border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-primary",
          )}
          {...props}
        />
        {value ? (
          <button type="button" onClick={clear} aria-label="Clear search"
            className={cn("absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md", dark ? "text-gray-500 hover:bg-gray-800 hover:text-gray-200" : "text-gray-400 hover:bg-gray-100 hover:text-gray-700")}>
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";

export default SearchInput;
